import React from 'react';
import { Card, Button, Typography, Spin, Avatar, Alert } from 'antd';
import { GoogleOutlined, LoadingOutlined, LogoutOutlined } from '@ant-design/icons';
import { useAuthState, useSignInWithGoogle } from 'react-firebase-hooks/auth';
import { signOut } from 'firebase/auth';
import { useNavigate, useLocation } from 'react-router-dom';
import { auth, googleProvider } from '../api/firebase';

const { Title, Text, Paragraph } = Typography;

const AuthGuard = ({ children }) => {
  const [user, loading, error] = useAuthState(auth);
  const [signInWithGoogle, , googleLoading, signInError] = useSignInWithGoogle(auth, googleProvider);
  const navigate = useNavigate();
  const location = useLocation();
  
  // Debug logging for production issues
  React.useEffect(() => {
    console.log('🔐 Auth State Debug:', {
      user: user ? 'Authenticated ✅' : 'Not authenticated ❌',
      loading: loading,
      error: error?.message,
      signInError: signInError?.message,
      currentURL: window.location.href
    });
    
    if (error) {
      console.error('❌ Auth Error:', error);
    }
    
    if (signInError) {
      console.error('❌ Sign-in Error:', signInError);
    }
  }, [user, loading, error, signInError]);

  // Redirect authenticated users away from login page
  React.useEffect(() => {
    if (user && !loading && location.pathname === '/login') {
      console.log('✅ User authenticated, redirecting to dashboard...');
      navigate('/', { replace: true });
    }
  }, [user, loading, location.pathname, navigate]);

  // Show loading during auth check
  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column',
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh',
        gap: '16px',
        background: 'linear-gradient(135deg, #1E90FF10, #87CEEB10)'
      }}>
        <Spin 
          size="large" 
          indicator={<LoadingOutlined style={{ fontSize: 48, color: '#1E90FF' }} spin />} 
        />
        <div style={{ 
          color: '#1E90FF', 
          fontSize: '18px', 
          fontWeight: 500,
          textAlign: 'center'
        }}>
          Checking authentication...
        </div>
      </div>
    );
  }

  // If user is authenticated and we're in protected content, render children
  if (user && children) {
    return children;
  }

  // If user is authenticated but no children (login page), show success message
  if (user && !children) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #1E90FF10, #87CEEB10)'
      }}>
        <Card
          style={{ 
            width: 400, 
            textAlign: 'center',
            borderRadius: '16px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.1)'
          }}
          cover={
            <div style={{ 
              padding: '40px', 
              background: 'linear-gradient(135deg, #52c41a, #73d13d)',
              color: 'white'
            }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>✅</div>
              <Title level={3} style={{ color: 'white', margin: 0 }}>
                Successfully Signed In!
              </Title>
            </div>
          }
        >
          <div style={{ padding: '0 0 20px' }}>
            <div style={{ marginBottom: '20px' }}>
              <Avatar 
                size={64} 
                src={user.photoURL} 
                style={{ marginBottom: '12px' }}
              />
              <Title level={4} style={{ margin: '8px 0 4px', color: '#000080' }}>
                Welcome, {user.displayName}!
              </Title>
              <Text type="secondary">{user.email}</Text>
            </div>
            
            <Alert
              message="Redirecting to your dashboard..."
              type="success"
              showIcon
              style={{ marginBottom: '20px' }}
            />
            
            <Button 
              type="primary"
              onClick={() => navigate('/')}
              style={{ 
                backgroundColor: '#1E90FF',
                borderColor: '#1E90FF',
                marginRight: '8px'
              }}
            >
              Go to Dashboard
            </Button>
            
            <Button 
              icon={<LogoutOutlined />}
              onClick={() => signOut(auth)}
              style={{ 
                borderColor: '#ff4d4f',
                color: '#ff4d4f'
              }}
            >
              Sign Out
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  // Show sign-in form for unauthenticated users
  return (
    <div style={{ 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'center', 
      minHeight: '100vh',
      padding: '20px',
      background: 'linear-gradient(135deg, #1E90FF10, #87CEEB10)'
    }}>
      <Card
        style={{ 
          width: '100%', 
          maxWidth: 450, 
          textAlign: 'center',
          borderRadius: '16px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.1)'
        }}
        cover={
          <div style={{ 
            padding: '40px', 
            background: 'linear-gradient(135deg, #1E90FF, #87CEEB)',
            color: 'white'
          }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>⚡</div>
            <Title level={2} style={{ color: 'white', margin: 0 }}>
              Progress Craft
            </Title>
            <Text style={{ color: 'rgba(255,255,255,0.9)', fontSize: '16px' }}>
              Your Ultimate Productivity Hub
            </Text>
          </div>
        }
      >
        <div style={{ padding: '0 0 20px' }}>
          <Title level={3} style={{ marginBottom: '16px', color: '#000080' }}>
            Welcome Back!
          </Title>
          
          <Paragraph style={{ 
            color: '#666', 
            marginBottom: '32px',
            fontSize: '16px',
            lineHeight: '1.5'
          }}>
            Sign in with your Google account to access your productivity tools, 
            track your habits, and continue your focus sessions.
          </Paragraph>

          {(error || signInError) && (
            <Alert
              message="Authentication Error"
              description={error?.message || signInError?.message || 'Something went wrong. Please try again.'}
              type="error"
              showIcon
              style={{ marginBottom: '24px' }}
            />
          )}

          <Button
            type="primary"
            size="large"
            icon={googleLoading ? <LoadingOutlined /> : <GoogleOutlined />}
            onClick={() => signInWithGoogle()}
            loading={googleLoading}
            disabled={googleLoading}
            style={{
              width: '100%',
              height: '50px',
              fontSize: '16px',
              fontWeight: 'bold',
              backgroundColor: '#1E90FF',
              borderColor: '#1E90FF',
              borderRadius: '8px'
            }}
          >
            {googleLoading ? 'Signing in...' : 'Continue with Google'}
          </Button>

          <div style={{ marginTop: '24px' }}>
            <Text style={{ color: '#999', fontSize: '12px' }}>
              By signing in, you agree to our Terms of Service and Privacy Policy.<br/>
              Your data is secure and encrypted with Google Firebase.
            </Text>
          </div>

          <div style={{ marginTop: '20px' }}>
            <Button 
              type="link"
              onClick={() => navigate('/')}
              style={{ color: '#1E90FF' }}
            >
              ← Back to Landing Page
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default AuthGuard; 